import DOMPurify from 'dompurify';

/**
 * Sanitiza HTML autorado por admin (editor Quill, texto colado do Word/e-mail) e o deixa
 * legível nos dois temas.
 *
 * Texto colado chega com `style="background-color: #fff; color: #000"` inline, que ganharia
 * do tema e viraria uma tarja branca no escuro. Aqui — na renderização, não no dado: a edição
 * continua vendo o original — saem fundo e cor NEUTROS (preto/branco/cinza), e o texto herda a
 * cor do tema. Cor com intenção (azul de título, realce amarelo) fica.
 */

// Instância própria: hook no DOMPurify global vazaria para qualquer outro sanitize do app.
const purifier = DOMPurify(window);

const NAMED_NEUTRALS = new Set([
  'black', 'white', 'gray', 'grey', 'silver', 'gainsboro', 'whitesmoke', 'dimgray', 'dimgrey',
  'darkgray', 'darkgrey', 'lightgray', 'lightgrey', 'snow', 'ivory', 'windowtext', 'window',
]);

// Diferença máxima entre canais RGB para a cor contar como cinza (inclui #333, #fafafa, #f8f9fa).
const NEUTRAL_SPREAD = 24;

function channels(value: string): number[] | null {
  const rgb = value.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/);
  if (rgb) return rgb.slice(1, 4).map(Number);
  const hex = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (!hex) return null;
  const digits = hex[1].length === 3 ? hex[1].replace(/./g, '$&$&') : hex[1];
  return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16));
}

export function isNeutralColor(value: string): boolean {
  const color = value.trim().toLowerCase();
  if (NAMED_NEUTRALS.has(color)) return true;
  const rgb = channels(color);
  return rgb !== null && Math.max(...rgb) - Math.min(...rgb) <= NEUTRAL_SPREAD;
}

purifier.addHook('afterSanitizeAttributes', (node) => {
  if (!(node instanceof HTMLElement) || !node.hasAttribute('style')) return;
  const { style } = node;
  if (style.backgroundColor && isNeutralColor(style.backgroundColor)) {
    style.removeProperty('background-color');
    style.removeProperty('background');
  }
  // Com realce colorido, a cor do texto foi escolhida para ele: as duas ficam juntas.
  if (style.color && isNeutralColor(style.color) && !style.backgroundColor) {
    style.removeProperty('color');
  }
  if (!style.length) node.removeAttribute('style');
});

export function sanitizeRichHtml(html: string): string {
  return purifier.sanitize(html);
}
